import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CircularProgress } from '@mui/material';
import { Button } from '../../components/UI/Button/Button';
import { useApi } from '../../hooks/useApi';
import { useStores } from '../../hooks/useStore';
import { Course, CourseModule } from '../../models/courses';

import styles from './courses.module.css';
import ModuleForm from '../../components/popUp/Courses/ModuleForm';

interface SortableModuleRowProps {
  module: CourseModule;
  onEdit: (_m: CourseModule) => void;
  onDelete: (_m: CourseModule) => void;
  onSelect: (_m: CourseModule) => void;
}

const SortableModuleRow: React.FC<SortableModuleRowProps> = ({
  module,
  onEdit,
  onDelete,
  onSelect,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.id });

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={rowStyle} className={styles.moduleRow}>
      <span
        className={styles.dragHandle}
        {...attributes}
        {...listeners}
        aria-label="Перетащить"
        role="button"
        tabIndex={0}
      >
        <svg
          width="16"
          height="22"
          viewBox="0 0 16 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="5" cy="4" r="1.5" fill="currentColor" />
          <circle cx="11" cy="4" r="1.5" fill="currentColor" />
          <circle cx="5" cy="11" r="1.5" fill="currentColor" />
          <circle cx="11" cy="11" r="1.5" fill="currentColor" />
          <circle cx="5" cy="18" r="1.5" fill="currentColor" />
          <circle cx="11" cy="18" r="1.5" fill="currentColor" />
        </svg>
      </span>

      <div className={styles.moduleRowInfo} onClick={() => onSelect(module)}>
        <span className={styles.moduleRowTitle}>{module.title}</span>
        {module.description && (
          <span className={styles.moduleRowDesc}>{module.description}</span>
        )}
      </div>

      <div
        className={styles.moduleRowActions}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Редактировать"
          onClick={() => onEdit(module)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
          </svg>
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Удалить"
          onClick={() => onDelete(module)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

interface CourseModulesProps {
  course: Course;
  onSelectModule: (_module: CourseModule) => void;
  onBack: () => void;
}

export const CourseModules: React.FC<CourseModulesProps> = ({
  course,
  onSelectModule,
  onBack,
}) => {
  const api = useApi();
  const store = useStores();

  const [modules, setModules] = useState<CourseModule[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedModule, setSelectedModule] = useState<CourseModule | null>(
    null,
  );
  const [openModuleForm, setOpenModuleForm] = useState<boolean>(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() => {
    loadModules();
    // eslint-disable-next-line
  }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      const { body } = await api.courses.getModules(course.id);
      if (body) {
        setModules([...body].sort((a, b) => a.sort_order - b.sort_order));
      }
    } catch {
      store.notificationsStore.setError('Ошибка при загрузке модулей');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (module: CourseModule) => {
    const confirmed = window.confirm(`Удалить модуль "${module.title}"?`);
    if (!confirmed) {
      return;
    }

    try {
      store.notificationsStore.showPreloader();
      await api.courses.deleteModule(module.id);
      await loadModules();
    } catch {
      store.notificationsStore.setError('Ошибка при удалении модуля');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = modules.findIndex((m) => m.id === active.id);
    const newIndex = modules.findIndex((m) => m.id === over.id);
    const reordered = arrayMove(modules, oldIndex, newIndex);
    setModules(reordered);

    try {
      await api.courses.reorderModules({ ids: reordered.map((m) => m.id) });
    } catch {
      store.notificationsStore.setError('Ошибка при изменении порядка модулей');
      setModules(modules);
    }
  };

  const handleEditModule = (module: CourseModule) => {
    setSelectedModule(module);
    setOpenModuleForm(true);
  };

  const handleCloseForm = () => {
    setOpenModuleForm(false);
    setSelectedModule(null);
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    loadModules();
  };

  return (
    <>
      <ModuleForm
        open={openModuleForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        courseId={course.id}
        module={selectedModule}
      />
      <div className={styles.container}>
        <div className={styles.pageName}>
          <div className={styles.top}>
            <div className={styles.title}>
              <h2>
                <span
                  onClick={onBack}
                  style={{ cursor: 'pointer', opacity: 0.5 }}
                >
                  Курсы
                </span>
                {' > '}
                {course.title}
              </h2>
            </div>
            <Button
              style={{ maxWidth: 180 }}
              appearance="auth"
              onClick={() => {
                setSelectedModule(null);
                setOpenModuleForm(true);
              }}
            >
              Добавить модуль
            </Button>
          </div>
        </div>

        {loading ? (
          <CircularProgress />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={modules.map((m) => m.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={styles.moduleList}>
                {modules.map((module) => (
                  <SortableModuleRow
                    key={module.id}
                    module={module}
                    onEdit={handleEditModule}
                    onDelete={handleDelete}
                    onSelect={onSelectModule}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </>
  );
};

export default CourseModules;
